import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, Users } from "lucide-react";
import { api } from "@/lib/apiClient";
import { fmtDate } from "@/components/BookingModal";

const money = (n) => `£${(n || 0).toLocaleString()}`;

function MoveControl({ a, options, onMoved }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!a.id || !options.length) return null;
  if (!open) return <button data-testid={`register-move-${a.id}`} onClick={() => setOpen(true)} className="text-xs text-cyan hover:text-white transition-colors">Move</button>;
  const save = async () => {
    setBusy(true); setErr("");
    try { await api.patch(`/admin/bookings/${a.id}/move`, { class_date: to }); onMoved(); }
    catch (e) { setErr(e.response?.data?.detail || "Couldn't move this booking"); setBusy(false); }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <select data-testid={`register-move-select-${a.id}`} value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md bg-ink-900 border border-white/12 px-2 py-1 text-xs text-ice outline-none">
        <option value="">Choose date…</option>
        {options.map((o) => <option key={o.class_date} value={o.class_date}>{fmtDate(o.class_date)}{o.capacity ? ` (${o.attendees.length}/${o.capacity})` : ""}</option>)}
      </select>
      <button data-testid={`register-move-confirm-${a.id}`} disabled={!to || busy} onClick={save} className="text-xs text-white bg-electric rounded-md px-2 py-1 disabled:opacity-50">{busy ? "…" : "Move"}</button>
      <button onClick={() => setOpen(false)} className="text-xs text-ice/60">Cancel</button>
      {err && <span className="text-xs text-red-300 w-full">{err}</span>}
    </span>
  );
}

function NotifyControl({ s }) {
  const [open, setOpen] = useState(false);
  const [newTime, setNewTime] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("");
  if (!s.attendees.length || !s.class_date) return null;
  if (!open) return <button data-testid={`register-notify-${s.id}`} onClick={() => setOpen(true)} className="text-xs text-cyan hover:text-white transition-colors mt-3">Email everyone booked</button>;
  const send = async () => {
    setState("sending");
    try {
      const { data } = await api.post("/admin/register/notify", { location_id: s.location_id, class_slug: s.class_slug, day: s.day, time: s.time, class_date: s.class_date, new_time: newTime || null, message });
      setState(`Sent to ${data.sent} of ${data.total}`);
    } catch (e) { setState(`error:${e.response?.data?.detail || "Couldn't send"}`); }
  };
  const err = state.startsWith("error:");
  const done = state.startsWith("Sent");
  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-ink-900/60 p-4 space-y-3" data-testid={`register-notify-form-${s.id}`}>
      <p className="text-sm text-white">Email the {s.attendees.length} {s.attendees.length === 1 ? "person" : "people"} booked on {fmtDate(s.class_date)}</p>
      <label className="block text-xs text-mist">New start time (optional)
        <input data-testid="notify-new-time" type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} className="mt-1 block rounded-md bg-ink-900 border border-white/12 px-2 py-1.5 text-sm text-ice outline-none" />
      </label>
      <textarea data-testid="notify-message" rows={3} maxLength={1500} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message (e.g. the studio is only free from 9am this week)" className="w-full rounded-md bg-ink-900 border border-white/12 px-3 py-2 text-sm text-ice placeholder:text-mist outline-none" />
      <div className="flex items-center gap-3">
        <button data-testid="notify-send" disabled={state === "sending" || done || (!newTime && !message.trim())} onClick={send} className="text-xs text-white bg-electric rounded-md px-3 py-1.5 disabled:opacity-50">{state === "sending" ? "Sending…" : "Send email"}</button>
        <button onClick={() => { setOpen(false); setState(""); }} className="text-xs text-ice/60">Close</button>
        {state && state !== "sending" && <span data-testid="notify-result" className={`text-xs ${err ? "text-red-300" : "text-cyan"}`}>{err ? state.slice(6) : state}</span>}
      </div>
    </div>
  );
}

function SessionRow({ s, moveOptions, onMoved }) {
  const [open, setOpen] = useState(false);
  const count = s.attendees.length;
  return (
    <div data-testid={`register-session-${s.id}`} className="rounded-xl border border-white/8 bg-ink-800">
      <button data-testid={`register-toggle-${s.id}`} onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-x-6 gap-y-2 p-4 text-left">
        <div className="flex-1 min-w-[200px]">
          <p className="text-white font-semibold">{s.class_name} <span className="text-mist font-normal text-sm">· {s.location_name}</span></p>
          <p className="text-ice/70 text-sm">{s.class_date ? fmtDate(s.class_date) : s.day} · {s.time}</p>
        </div>
        <span className="flex items-center gap-1.5 text-sm text-ice/80" data-testid={`register-count-${s.id}`}><Users size={14} className="text-cyan" /> {count}{s.capacity ? ` / ${s.capacity}` : ""}</span>
        <span className="text-white font-semibold text-sm w-16 text-right" data-testid={`register-income-${s.id}`}>{money(s.income)}</span>
        <ChevronDown size={16} className={`text-ice/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-white/8 px-4 pb-4">
          {count ? (
            <table className="w-full text-sm mt-2">
              <thead><tr className="text-left text-mist text-xs"><th className="py-2">Name</th><th className="py-2 hidden sm:table-cell">Email</th><th className="py-2">Paid by</th><th className="py-2 text-right">Amount</th><th className="py-2 w-28" /></tr></thead>
              <tbody>
                {s.attendees.map((a, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-2 text-white">{a.name}</td>
                    <td className="py-2 text-ice/70 hidden sm:table-cell">{a.email}</td>
                    <td className="py-2 text-ice/70">{a.method}{a.code ? <span className="text-mist text-xs"> · {a.code}</span> : null}</td>
                    <td className="py-2 text-right text-ice">{money(a.amount)}</td>
                    <td className="py-2 pl-3 text-right"><MoveControl a={a} options={moveOptions} onMoved={onMoved} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-mist text-sm mt-3">No bookings yet.</p>}
          {(s.credits_used > 0 || s.free_codes > 0) && <p className="text-mist text-xs mt-3">{s.credits_used} paid with credits · {s.free_codes} free codes</p>}
          <NotifyControl s={s} />
        </div>
      )}
    </div>
  );
}

export function ClassRegister() {
  const [rows, setRows] = useState(null);
  const [view, setView] = useState("upcoming");
  const [loc, setLoc] = useState("");
  const load = () => api.get("/admin/register").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const locations = useMemo(() => [...new Map((rows || []).map((r) => [r.location_id, r.location_name])).entries()], [rows]);
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
  const shown = useMemo(() => {
    const list = (rows || []).filter((r) => (!loc || r.location_id === loc) && (view === "upcoming" ? (r.class_date || "") >= today : (r.class_date || "") < today));
    return view === "past" ? [...list].reverse() : list;
  }, [rows, view, loc, today]);
  if (!rows) return <Loader2 className="animate-spin text-cyan" />;
  const total = shown.reduce((a, s) => a + s.income, 0);
  const people = shown.reduce((a, s) => a + s.attendees.length, 0);
  return (
    <div className="space-y-4" data-testid="admin-register">
      <div className="flex flex-wrap items-center gap-3">
        {["upcoming", "past"].map((v) => (
          <button key={v} data-testid={`register-view-${v}`} onClick={() => setView(v)}
            className={`rounded-full px-4 py-2 text-sm capitalize transition-colors ${view === v ? "bg-electric text-white" : "bg-ink-800 text-ice/70 hover:text-white"}`}>{v}</button>
        ))}
        <select data-testid="register-location" value={loc} onChange={(e) => setLoc(e.target.value)} className="rounded-lg bg-ink-900 border border-white/12 px-3 py-2 text-sm text-ice outline-none focus:border-cyan/60">
          <option value="">All locations</option>
          {locations.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <span className="ml-auto text-sm text-ice/70" data-testid="register-totals">{shown.length} classes · {people} attendees · {money(total)}</span>
      </div>
      {shown.map((s) => (
        <SessionRow key={s.id} s={s} onMoved={load}
          moveOptions={rows.filter((o) => o.location_id === s.location_id && o.class_slug === s.class_slug && o.day === s.day && o.time === s.time && (o.class_date || "") >= today && o.class_date !== s.class_date)} />
      ))}
      {shown.length === 0 && <p className="text-mist">No {view} classes.</p>}
    </div>
  );
}
